/**
 * Token Processing Hook
 *
 * Handles validation and processing of invitation tokens.
 * Manages token state and provides methods for token-based actions.
 *
 * Features:
 * - Automatic token validation via API
 * - Token status tracking (valid, invalid, completed)
 * - Error handling and status reporting
 * - Token data storage in session
 * - Authentication flow integration for token acceptance
 * - Pre-fetches value lists in parallel for faster display
 */
// src/hooks/useTokenProcessor.js
import { useState, useEffect } from 'react';
import { processInvitationToken, storeTokenData, storePendingAcceptance } from '@/services/token/tokenService';
import { appStore } from '@/services/app/store';

/**
 * Hook to handle token processing logic
 * 
 * @param {string|null} token - Token from URL parameter
 * @returns {Object} Token processing state and methods
 */
export const useTokenProcessor = (token) => {
  // Token processing state
  const [processing, setProcessing] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Pre-fetch value lists needed for invitation display
   * Runs in parallel with token validation for faster UI
   */
  const prefetchValueLists = async () => {
    try {
      const promises = [];

      // Pre-fetch countries and hotspotSectors if not already loaded
      if (!appStore.state.data.countries?.length) {
        promises.push(appStore.valueListHandler.loadValueList('countries').catch(() => {}));
      }
      if (!appStore.state.data.hotspotSectors?.length) {
        promises.push(appStore.valueListHandler.loadValueList('hotspotSectors').catch(() => {}));
      }

      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }
    } catch {
      // Silent fail - ValidTokenCard will load these as fallback
    }
  };

  /**
   * Process token on mount if provided
   * Validates the token and pre-fetches value lists in parallel
   */
  useEffect(() => {
    if (!token) return;

    const processToken = async () => {
      setProcessing(true);

      try {
        // Run token validation AND value list pre-fetch in parallel
        const [result] = await Promise.all([
          processInvitationToken(token),
          prefetchValueLists()
        ]);

        setTokenData(result.data);

        // Store valid token data
        if (result.status === 'VALID') {
          storeTokenData({
            ...result.data,
            InvitationToken: token
          });
          setTokenStatus('valid');
        } else if (result.status === 'ALREADY_REGISTERED') {
          setTokenStatus('completed');
        } else {
          setError(result.message || 'Invalid invitation token');
          setTokenStatus('invalid');
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Token processing error:', err);
        }
        setError('Failed to process invitation token');
        setTokenStatus('invalid');
      } finally {
        setProcessing(false);
      }
    };

    processToken();
  }, [token]);
  
  /**
   * Helper method for login to accept invitation
   * Stores pending acceptance data and redirects to login
   * 
   * @param {string} linkingId - ID for linking the invitation to the user
   * @param {Object} tokenData - Token data to be stored for post-login processing
   * @returns {Promise<boolean>} Success status
   */
  const loginToAccept = async (linkingId, tokenData) => {
    try {
      // Store pending acceptance
      storePendingAcceptance(linkingId, tokenData);
      
      // Redirect to login page
      setTimeout(() => {
        window.location.href = '/?login=accept';
      }, 200);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to store acceptance:', error);
      }
      window.location.href = `/?login=accept&pendingLinkingId=${linkingId}`;
      return false;
    }
  };

  return {
    processing,
    tokenData,
    tokenStatus,
    error,
    loginToAccept
  };
};